import PageTransition from "@/components/ui/PageTransition";
import SubmitButton from "@/components/ui/submit-button";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/auth-context";
import useSendPhoneVerificationCode from "@/features/register/hooks/useSendPhoneVerificationCode";
import { getHelpCenterUrl } from "@/lib/utils";
import { Smartphone } from "lucide-react";
import { useEffect, useState } from "react";
import { VerifyPhoneChangePhoneSection } from "./VerifyPhoneChangePhoneSection";
import { useVerifyAccountStep } from "./useVerifyAccountStep";

export default function VerifyPhonePromptPage() {
	const { user } = useAuth();
	const { setStep } = useVerifyAccountStep();
	const { toast } = useToast();
	const [progress, setProgress] = useState(15);

	const sendCode = useSendPhoneVerificationCode();

	useEffect(() => {
		const timer = setTimeout(() => setProgress(30), 20);
		return () => clearTimeout(timer);
	}, []);

	const handleSendCode = () => {
		sendCode.mutate(
			{
				phone_number: user.phone_number.toString(),
				global_country_id: user.phone_global_country_id,
			},
			{
				onSuccess: () => {
					setStep("verify_phone_code");
					toast({
						title: "Code sent",
						description: "A verification code has been sent to your phone.",
					});
				},
				onError: (error) => {
					toast({
						title: "Failed to send code",
						description: error.message,
						variant: "destructive",
					});
				},
			},
		);
	};

	return (
		<PageTransition>
			<div className="flex flex-col items-center justify-between min-h-[100svh] px-6 py-8 bg-white text-textDark">
				{/* Progress */}
				<div className="flex justify-center w-full pt-4">
					<Progress value={progress} className="max-w-16" />
				</div>

				{/* Content */}
				<div className="flex flex-1 flex-col items-center justify-center w-full max-w-sm text-center">
					<div className="flex size-14 items-center justify-center rounded-full bg-primary-50 text-primary-600 mb-6">
						<Smartphone className="size-7" aria-hidden />
					</div>
					<h1 className="text-2xl font-bold tracking-tight text-textDark mb-2">
						Verify your phone number
					</h1>
					<p className="text-sm text-textLight mb-8 leading-relaxed">
						We’ll send a one-time code to the number below. Tap the button when
						you’re ready to receive it.
					</p>

					<VerifyPhoneChangePhoneSection
						sendCodeButton={
							<SubmitButton
								type="button"
								testId="send-code-button"
								mutationStatus={sendCode.status}
								onClick={handleSendCode}
								loadingText="Sending..."
								className="w-full text-base font-semibold text-white bg-primary-600 rounded-xl py-3"
							>
								Send code via text
							</SubmitButton>
						}
					/>
				</div>

				{/* Support */}
				<a
					href={getHelpCenterUrl()}
					target="_blank"
					rel="noopener noreferrer"
					className="text-sm text-center w-full block cursor-pointer pb-2"
				>
					<span className="text-textDark">Having issues?</span>{" "}
					<span className="text-textDark font-semibold">Contact Support</span>
				</a>
			</div>
		</PageTransition>
	);
}
